import {
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  DEFAULT_SPACE_NAME,
  plainSpaceContent,
  type InviteKind,
  type SpaceContent,
} from "@vmeste/shared";
import { useSession } from "../app/session";
import { useMembers } from "../app/lists";
import { useEncryptionMode, useSpaceKey } from "../app/encryption";
import { ACCEPT_IMAGES, MAX_ATTACHMENT_BYTES } from "../app/attachments";
import { ROLE_LABEL } from "../app/views";
import { ApiError, api } from "../lib/api";
import { keyMaterialFor } from "../lib/encryption";
import { syncEngine } from "../lib/sync";
import { Button, Card, CardHeader, TextField } from "../components/ui";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { NotificationSettings } from "../components/NotificationSettings";
import { EncryptionCard } from "../components/EncryptionCard";
import { QrCode } from "../components/QrCode";
import { THEMES, THEME_LABEL, useTheme } from "../app/theme";
import {
  CameraIcon,
  CheckIcon,
  ChevronDownIcon,
  CopyIcon,
  InviteAppIcon,
  InviteSpaceIcon,
  LogOutIcon,
  PlusIcon,
  QrIcon,
  ShareIcon,
} from "../components/icons";
import { cn } from "../lib/cn";
import {
  LOCALES,
  LOCALE_LABEL,
  setLocaleChoice,
  useDateFormats,
  useLocaleChoice,
  useT,
} from "../i18n/index.js";

/** The store's ceiling, in the unit the hint says it in. */
const PHOTO_MB = Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024);

/**
 * Put somebody out of this space. Owner only, and never yourself.
 *
 * The button was here from the start with `disabled` on it and nothing behind
 * it — a switch that controls nothing, which SPEC §4.2 argues against by name:
 * people turn it on and wait. Both halves already existed (the route and the
 * client call); nothing ever joined them.
 *
 * A dialog rather than the two-press confirm most destructive things here use.
 * Two presses are right when the cost lands on you; this one lands on somebody
 * else, who finds out by the space disappearing from their phone.
 */
function RemoveMember({
  spaceId,
  user,
}: {
  spaceId: string;
  user: { id: string; displayName: string };
}) {
  const t = useT();
  const { refresh } = useSession();
  const [asking, setAsking] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function remove() {
    setBusy(true);
    setFailed(null);
    try {
      await api.removeMember(spaceId, user.id);
      setAsking(false);
      // The membership list is the server's word, and the delta carries the
      // space rather than its members — so ask again rather than guess.
      await syncEngine.pull(spaceId).catch(() => undefined);
      await refresh().catch(() => undefined);
    } catch (cause) {
      setFailed(cause instanceof ApiError ? cause.message : t("settings.removeMemberFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => setAsking(true)}
        aria-label={t("settings.removeMemberOf", { name: user.displayName })}
        className="px-3 text-[0.875rem] text-muted hover:text-neg"
      >
        {t("settings.removeMember")}
      </Button>

      {asking ? (
        <ConfirmDialog
          title={t("settings.removeMemberTitle", { name: user.displayName })}
          confirmLabel={busy ? t("settings.removingMember") : t("settings.removeMember")}
          onCancel={() => setAsking(false)}
          onConfirm={() => void remove()}
        >
          <p>{t("settings.removeMemberWhat", { name: user.displayName })}</p>
          <p className="text-neg">{t("settings.removeMemberBack")}</p>
          {failed ? (
            <p role="alert" className="text-neg">
              {failed}
            </p>
          ) : null}
        </ConfirmDialog>
      ) : null}
    </>
  );
}

export function SettingsPage() {
  const { session, space, signOut } = useSession();
  const members = useMembers(space?.id ?? null);
  const formats = useDateFormats();
  const t = useT();
  if (!session || !space) return null;

  return (
    <div className="flex flex-col gap-4 px-4 pt-4 pb-8">
      <h1 className="px-1 font-display text-2xl">{t("settings.title")}</h1>

      <ProfileCard />
      <SpaceCard />

      <Card>
        <CardHeader title={t("settings.members")} meta={ROLE_LABEL[space.role]} />
        <ul className="divide-y divide-line-soft">
          {members.map(({ user, role, joinedAt }) => (
            <li key={user.id} className="flex items-center gap-3 px-4 py-3">
              <Avatar name={user.displayName} src={user.avatarUrl} />
              <div className="min-w-0 flex-1">
                <p className="truncate">
                  {user.displayName}
                  {user.id === session.user.id ? (
                    <span className="text-muted"> {t("settings.itsYou")}</span>
                  ) : null}
                </p>
                <p className="text-[0.8125rem] text-muted">
                  {ROLE_LABEL[role]} ·{" "}
                  {t("settings.joined", {
                    date: formats.dayMonthYearLong.format(new Date(joinedAt)),
                  })}
                </p>
              </div>
              {space.role === "owner" && user.id !== session.user.id ? (
                <RemoveMember spaceId={space.id} user={user} />
              ) : null}
            </li>
          ))}
        </ul>
      </Card>

      <InvitePanel />
      <NewSpaceCard />
      <ThemeChoice />
      <LanguageChoice />
      <InstallHint />
      <NotificationSettings />
      <ActivityLink />

      <EncryptionCard />

      <Button variant="secondary" full onClick={signOut} className="text-neg">
        <LogOutIcon className="size-5" />
        {t("settings.signOut")}
      </Button>

      <p className="px-1 text-center text-[0.75rem] text-muted">
        {session.user.email}
      </p>
    </div>
  );
}

/**
 * Your own name and face.
 *
 * First card on the screen, because until now the name typed at registration
 * was permanent and the avatar column was never filled by anything — a person
 * who married, or who simply typed их имя in a hurry, had no way back.
 *
 * Both writes go to `/api/profile`, which acts on the signed-in person and has
 * no way to name anyone else. The picture is uploaded on its own request rather
 * than through the outbox, for the reason attachments are: that queue lives in
 * localStorage and holds small JSON operations, and a photo has no business in
 * it. So an upload attempted offline fails outright instead of waiting.
 *
 * On screen it is one row of controls and one line of words. Everything either
 * acts or explains: the picture is the button that changes it, the name field
 * stands beside it rather than under it, and Save shows up only when the name
 * has actually changed — see `SaveButton`.
 */
function ProfileCard() {
  const { session, space, refresh } = useSession();
  const t = useT();
  const fileInput = useRef<HTMLInputElement>(null);

  const user = session?.user;
  const [name, setName] = useState(user?.displayName ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  // The name arrives after the first `me` call, and can change on another
  // device; the field follows the server unless it is being edited right now.
  useEffect(() => {
    if (!user) return;
    setName((current) => (saving ? current : user.displayName));
  }, [user?.displayName]);

  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), 2000);
    return () => clearTimeout(timer);
  }, [saved]);

  if (!user) return null;

  /**
   * Pull the space this screen is showing, so the member list below picks the
   * change up on this device too. The server nudges every space over the socket
   * as well; this is what makes it immediate when the socket is asleep.
   */
  const adopt = async () => {
    await refresh();
    if (space) await syncEngine.pull(space.id);
  };

  const saveName = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();

    if (trimmed.length === 0) {
      setNameError(t("settings.profileNameEmpty"));
      return;
    }
    if (trimmed === user.displayName) return;

    setSaving(true);
    setNameError(null);
    try {
      await api.updateProfile({ displayName: trimmed });
      // The server took the trimmed name, so the field should show it too —
      // otherwise a stray trailing space keeps the Save button alive forever.
      setName(trimmed);
      await adopt();
      setSaved(true);
    } catch (cause) {
      setNameError(
        cause instanceof ApiError ? cause.message : t("settings.profileSaveFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  const choosePhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // The same input is used again for the next picture, and it would otherwise
    // refuse to fire for the same file twice.
    event.target.value = "";
    if (!file) return;

    setPhotoError(null);

    // Refused here rather than after the bytes have gone up the wire. The same
    // two rules the server applies, mirrored so the picker cannot offer a file
    // that is going to come back as a 400.
    const mime = file.type.split(";")[0]?.trim().toLowerCase() ?? "";
    if (!ACCEPT_IMAGES.split(",").includes(mime)) {
      setPhotoError(t("settings.profilePhotoUnsupported"));
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setPhotoError(t("settings.profilePhotoTooLarge", { size: PHOTO_MB }));
      return;
    }

    setUploading(true);
    try {
      await api.uploadAvatar(file);
      await adopt();
    } catch (cause) {
      setPhotoError(
        cause instanceof ApiError ? cause.message : t("settings.profilePhotoFailed"),
      );
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    setUploading(true);
    setPhotoError(null);
    try {
      await api.removeAvatar();
      await adopt();
    } catch (cause) {
      setPhotoError(
        cause instanceof ApiError ? cause.message : t("settings.profilePhotoFailed"),
      );
    } finally {
      setUploading(false);
    }
  };

  // What the button's presence means: there is something to save. Empty is not
  // something to save — it is the one thing the field may not hold — so Enter
  // is left to raise that error rather than a button that looks ready.
  const changed = name.trim().length > 0 && name.trim() !== user.displayName;

  return (
    <Card>
      <CardHeader
        title={t("settings.profile")}
        meta={<Saved on={saved} word={t("settings.profileSaved")} />}
      />
      {/* gap-3 everywhere a card stacks blocks, so the cards down this screen
          keep one rhythm rather than each choosing its own. */}
      <div className="flex flex-col gap-3 px-4 py-4">
        {/*
          The face and the name are one line, not two. The picture *is* the
          button that changes it — a camera badge in its corner says so — which
          is what let the row of photo buttons that used to sit beside it go.
          The picker button lives inside the form and says `type="button"`, so
          it opens the file dialog rather than saving the name.
        */}
        <form onSubmit={saveName} className="flex items-center gap-3">
          <button
            type="button"
            disabled={uploading}
            aria-busy={uploading || undefined}
            aria-label={
              uploading
                ? t("settings.profilePhotoUploading")
                : user.avatarUrl
                  ? t("settings.profilePhotoReplace")
                  : t("settings.profilePhotoChoose")
            }
            onClick={() => fileInput.current?.click()}
            // `flex` so the picture inside is not sitting on a text baseline:
            // the few pixels of descender under it would put the badge off the
            // circle. The circle is 64px, well over the touch floor.
            className="relative flex min-h-[var(--touch)] shrink-0 rounded-full"
          >
            <Avatar name={user.displayName} src={user.avatarUrl} size="lead" />
            <span
              aria-hidden="true"
              className={cn(
                "absolute right-0 bottom-0 grid size-7 place-items-center",
                "rounded-full bg-surface text-ink-soft",
                uploading && "animate-pulse",
              )}
            >
              <CameraIcon className="size-4" strokeWidth={1.8} />
            </span>
          </button>

          <div className="min-w-0 flex-1">
            <TextField
              label={t("settings.profileName")}
              value={name}
              maxLength={60}
              autoComplete="name"
              onChange={(event) => {
                setName(event.target.value);
                setNameError(null);
              }}
              error={nameError ?? undefined}
            />
          </div>

          <SaveButton
            show={changed}
            saving={saving}
            label={t("settings.profileSave")}
            savingLabel={t("settings.profileSaving")}
          />
        </form>

        <input
          ref={fileInput}
          type="file"
          accept={ACCEPT_IMAGES}
          onChange={choosePhoto}
          className="hidden"
        />

        {photoError ? <p className="text-[0.8125rem] text-neg">{photoError}</p> : null}

        {/* Taking the picture off is the one photo action a badge cannot carry —
            44px of target on a 64px circle would swallow the circle — so it
            shares the line the note was taking anyway, and only when there is a
            picture to take off. */}
        <div className="flex items-center gap-3">
          <p className="min-w-0 flex-1 text-[0.8125rem] text-muted">
            {t("settings.profileNote")}
          </p>
          {user.avatarUrl ? (
            <Button
              variant="ghost"
              disabled={uploading}
              onClick={removePhoto}
              className="shrink-0 px-3 text-[0.875rem] text-muted"
            >
              {t("settings.profilePhotoRemove")}
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

/**
 * The button that saves one field, standing on that field's own line.
 *
 * It is here only while there is something to save. A button that would do
 * nothing has no business holding a 44px row open under a field somebody edits
 * twice a year — and its disappearing is half the confirmation that the save
 * happened, the other half being the word in the card's header.
 *
 * What does *not* come and go is its column: 44px wide whether or not the
 * button is in it, so the field never narrows under a thumb halfway through a
 * word. The blank label above it is the same idea — it mirrors the field's own
 * label, which is what keeps the button on the input's line no matter what
 * appears underneath the field.
 *
 * A tick rather than the word: at this width the word does not fit beside a
 * name field on a 320px phone, and what it says is in `aria-label`.
 */
function SaveButton({
  show,
  saving,
  label,
  savingLabel,
}: {
  show: boolean;
  saving: boolean;
  label: string;
  savingLabel: string;
}) {
  return (
    <div className="flex w-[var(--touch)] shrink-0 flex-col gap-1.5 self-start">
      <span aria-hidden="true" className="px-1 text-[0.8125rem] font-medium">
        &nbsp;
      </span>
      {show ? (
        <Button
          type="submit"
          disabled={saving}
          aria-label={saving ? savingLabel : label}
          className={cn("w-full px-0", saving && "animate-pulse")}
        >
          <CheckIcon aria-hidden="true" className="size-5 shrink-0" />
        </Button>
      ) : null}
    </div>
  );
}

/**
 * "Saved", in the card's header.
 *
 * Above the field rather than under it, and in a slot that is there whether or
 * not it has a word in it: nothing on the screen moves when the confirmation
 * arrives or when it goes two seconds later. Mounted always, so a reader is
 * already watching this spot before the word appears.
 *
 * `otherwise` is what the slot says the rest of the time — the space card keeps
 * its role there, and lends it for the two seconds after a rename. Nobody is
 * reading «Владелец» in the moment they have just renamed the space.
 */
function Saved({
  on,
  word,
  otherwise = "",
}: {
  on: boolean;
  word: string;
  otherwise?: string;
}) {
  return <span role="status">{on ? word : otherwise}</span>;
}

/**
 * What this space is called.
 *
 * The name lives *inside* the space's ContentBlob rather than in a column of
 * its own (SPEC §3), so writing it goes through this space's codec exactly like
 * a list title does. A locked space — `e2ee`, no key on this device — has no
 * codec, and then there is nothing to write the name with; the block says so
 * instead of offering a field that would fail on submit.
 *
 * A member sees the name and one line about why it is not theirs to change,
 * rather than a greyed-out field. Same choice as the invite block below it: a
 * disabled control tells you that you cannot, never that somebody else can.
 */
function SpaceCard() {
  const { space, refresh } = useSession();
  const t = useT();

  const [name, setName] = useState(space?.name ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The name changes on another device, and switching spaces changes it here;
  // either way the field follows unless it is being saved right now.
  useEffect(() => {
    if (!space) return;
    setName((current) => (saving ? current : space.name));
  }, [space?.id, space?.name]);

  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), 2000);
    return () => clearTimeout(timer);
  }, [saved]);

  if (!space) return null;

  const codec = syncEngine.codec(space.id);
  const blocked =
    space.role !== "owner"
      ? t("settings.spaceOwnerOnly")
      : codec === null
        ? t("settings.spaceLocked")
        : null;

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (blocked || !codec) return;

    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError(t("settings.spaceNameEmpty"));
      return;
    }
    if (trimmed === space.name) return;

    setSaving(true);
    setError(null);
    try {
      const content = await codec.encode({ name: trimmed } satisfies SpaceContent);
      await api.renameSpace(space.id, content);
      // The server took the trimmed name, so the field shows it too — otherwise
      // a trailing space keeps the Save button alive forever.
      setName(trimmed);
      // `me` is what the switcher and this card read the name from; the pull
      // brings the same change to everything reading the space through sync.
      await refresh();
      await syncEngine.pull(space.id);
      setSaved(true);
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : t("settings.spaceSaveFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  // Same rule as the profile's: the button is there when pressing it would do
  // something, and an empty name is not that.
  const changed = name.trim().length > 0 && name.trim() !== space.name;

  return (
    <Card>
      <CardHeader
        title={t("settings.space")}
        meta={
          <Saved
            on={saved}
            word={t("settings.spaceSaved")}
            otherwise={ROLE_LABEL[space.role]}
          />
        }
      />
      <div className="flex flex-col gap-3 px-4 py-4">
        {blocked ? (
          <>
            <p className="rounded-row bg-surface-alt px-4 py-3">{space.name}</p>
            <p className="text-[0.8125rem] text-muted">{blocked}</p>
          </>
        ) : (
          // The field keeps its label and its full width; only the button that
          // was under it moved onto its line, and only for as long as the name
          // differs from the one that is saved.
          <form onSubmit={save} className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <TextField
                label={t("settings.spaceNameField")}
                value={name}
                maxLength={120}
                onChange={(event) => {
                  setName(event.target.value);
                  setError(null);
                }}
                error={error ?? undefined}
              />
            </div>
            <SaveButton
              show={changed}
              saving={saving}
              label={t("settings.spaceSave")}
              savingLabel={t("settings.spaceSaving")}
            />
          </form>
        )}

        <p className="text-[0.8125rem] text-muted">{t("settings.spaceNote")}</p>
      </div>
    </Card>
  );
}

/**
 * One more space, with this person as its owner.
 *
 * Two things make this more than a button:
 *
 *   - **The name is asked for here**, before the space exists. Creating an
 *     unnamed space and offering to rename it afterwards means two steps to say
 *     one thing, and leaves «Наше пространство» sitting in the switcher for
 *     everyone who skips the second step. Left blank it is that default — taken
 *     from `plainSpaceContent`, the same function registration and the
 *     bootstrap CLI use, so the wording lives in one place.
 *   - **Nothing is encrypted yet.** A brand-new space has no key and therefore
 *     no codec, so its first blob is written by `plainSpaceContent` rather than
 *     through `syncEngine.codec`, which would have nothing to answer with. The
 *     server creates it in `none` mode, which is what that blob says.
 *
 * Afterwards the session is refreshed and the new space selected, in that
 * order: the list `me` returns is what the switcher reads, and selecting an id
 * it has not heard of yet would be dropped. Landing in the new space is the
 * whole confirmation — without it a person is left looking at the old space
 * wondering whether anything happened at all.
 *
 * Folded away behind its own title, because a field, a hint and a button cost
 * the same third of a phone screen every single visit to Settings, and a second
 * space is made once or twice in a lifetime of using this. It folds itself back
 * up on success: the app is already standing in the new space by then, and an
 * emptied field left open is clutter that has finished its job.
 */
function NewSpaceCard() {
  const { refresh, selectSpace } = useSession();
  const t = useT();
  const panelId = useId();

  /*
   * The space switcher sends people here, and folding this away made that
   * journey end on a long screen with nothing visibly new on it — a menu item
   * that read as broken. So the switcher asks for this card by name and the
   * card answers: open, in view, cursor in the field.
   */
  const [params, setParams] = useSearchParams();
  const asked = params.get("new") === "space";

  const [open, setOpen] = useState(asked);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!asked) return;

    setOpen(true);
    cardRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    nameRef.current?.focus();

    // Dropped from the address once acted on: coming back to Settings later, or
    // reloading, should not re-open a form nobody asked for a second time.
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("new");
        return next;
      },
      { replace: true },
    );
  }, [asked, setParams]);

  useEffect(() => {
    if (!created) return;
    const timer = setTimeout(() => setCreated(null), 4000);
    return () => clearTimeout(timer);
  }, [created]);

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const { space } = await api.createSpace(plainSpaceContent(name));
      await refresh();
      selectSpace(space.id);
      setName("");
      setCreated(name.trim() || DEFAULT_SPACE_NAME);
      setOpen(false);
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : t("settings.spaceNewFailed"),
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card ref={cardRef}>
      <button
        type="button"
        onClick={() => setOpen((shown) => !shown)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex min-h-[var(--touch)] w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-alt"
      >
        <span className="min-w-0 flex-1 font-display text-[1.0625rem]">
          {t("settings.spaceNew")}
        </span>
        <ChevronDownIcon
          aria-hidden="true"
          className={cn(
            "size-5 shrink-0 text-muted transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {/*
        Kept in the tree and hidden rather than unmounted, so that the id
        `aria-controls` points at is always a real element — and so a half-typed
        name survives a stray tap on the title.
      */}
      <div id={panelId} hidden={!open}>
        <form onSubmit={create} className="flex flex-col gap-3 px-4 pb-4">
          <p className="text-[0.875rem] text-muted">{t("settings.spaceNewWhat")}</p>

          <TextField
            ref={nameRef}
            label={t("settings.spaceNewName")}
            value={name}
            maxLength={120}
            hint={t("settings.spaceNewHint", { name: DEFAULT_SPACE_NAME })}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
          />

          {error ? <p className="text-[0.875rem] text-neg">{error}</p> : null}

          <Button
            type="submit"
            variant="secondary"
            disabled={creating}
            className="self-start"
          >
            <PlusIcon className="size-5" strokeWidth={1.8} />
            {creating ? t("settings.spaceNewCreating") : t("settings.spaceNewCreate")}
          </Button>
        </form>
      </div>

      {/* The confirmation outlives the form it came from, which folds itself
          away the moment the space exists. */}
      {created ? (
        <p role="status" className="px-4 pb-3 text-[0.8125rem] text-muted">
          {t("settings.spaceNewDone", { space: created })}
        </p>
      ) : null}
    </Card>
  );
}

/**
 * The two ways in (SPEC §4.1), as one choice.
 *
 * A code into this space and a code into the app are the same ten characters in
 * a messenger, and only one of them lets a stranger read the lists, the chat and
 * the calendar. Mixing them up fails in exactly one direction — you meant "come
 * and use this app", and you handed over a key to your own home.
 *
 * This used to be two identical blocks stacked, each with its own full-width
 * button; on a phone that pushed everything below it out of reach. It is one
 * block now, because a person here is making **one** decision — who am I
 * inviting — and a decision between two things is a choice, not two offers. The
 * safeguards that decision needs are all still here, and none of them is the
 * word on a button:
 *
 *   - the two names say where the other person ends up, never what the code is
 *     called. The pill has to stay narrow enough for a 320px phone, so the
 *     space's own name («В пространство «Дом»») rides in the accessible name,
 *     which is exactly where a reader announcing two near-identical options
 *     needs it;
 *   - under the pills, one sentence about what the chosen person will see —
 *     shown for the selected option only, because two consequences on screen at
 *     once is what made this panel tall, and only one of them is being acted on;
 *   - that sentence sits in an **amber** block for the option that opens this
 *     space, and a grey one for the option that opens nothing of yours. Amber is
 *     the only colour in this interface that means anything, and here it means
 *     "this is your home you are handing out";
 *   - a code appears directly under that sentence and nowhere else. There is
 *     never a second code on screen to confuse it with — which is what let the
 *     old duplicate line under the code go.
 *
 * Codes are minted on request rather than on page load: every one is a live way
 * in, and generating them because somebody opened Settings would leave a trail
 * of valid invites nobody asked for. Both minted codes are kept, per kind, so
 * that flipping to the other option and back does not throw away a code that
 * has not been passed on yet.
 *
 * What leaves this screen is a **message**, not a code. Handing over the ten
 * characters, or even the link, left the sentence around them to be written by
 * hand every single time — and the sentence is the part that has to say which
 * of the two doors this is. So the button builds the whole thing, and on a
 * phone it goes straight into the system share sheet, which is where an
 * invitation is actually sent from.
 *
 * Into this space there is a second way out, beside the first: the same
 * invitation as a square (`InviteQr`), behind the one icon button in this card.
 * Two outputs, because they are not interchangeable — see the comment on
 * `InviteQr` for the one rule that keeps them apart.
 */
function InvitePanel() {
  const { space } = useSession();
  const formats = useDateFormats();
  const t = useT();
  const squareId = useId();

  const [kind, setKind] = useState<InviteKind>("space");
  // Per kind, not one slot: minting the second code must not sweep the first
  // off the screen before it has been passed on to anybody.
  //
  // The whole invite is kept, not just its code: the message says when the
  // thing stops working, and that date is the server's answer rather than this
  // screen's arithmetic over a "7 days" it does not control.
  const [codes, setCodes] = useState<Record<InviteKind, Issued | null>>({
    space: null,
    app: null,
  });
  const [minting, setMinting] = useState<InviteKind | null>(null);
  const [copied, setCopied] = useState<CopiedWhat | null>(null);
  const [failure, setFailure] = useState<{ kind: InviteKind; message: string } | null>(
    null,
  );
  const [square, setSquare] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(null), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const spaceId = space?.id;
  const spaceName = space?.name ?? "";
  const isOwner = space?.role === "owner";

  // Switching space takes everything minted here down with it. A code for the
  // space that was open a moment ago, sitting under the new space's name, is
  // the one mistake this whole panel is written to prevent.
  useEffect(() => {
    setCodes({ space: null, app: null });
    setSquare(false);
    setFailure(null);
    setCopied(null);
  }, [spaceId]);

  async function mint(kind: InviteKind) {
    if (kind === "space" && !spaceId) return;
    setMinting(kind);
    setFailure(null);
    try {
      // The space id is what tells the two apart on the wire, and it is passed
      // only on the branch that means it — an app code carries none at all.
      const { invite } = await api.createInvite(
        kind === "space" && spaceId ? spaceId : { kind: "app" },
      );
      setCodes((current) => ({
        ...current,
        [kind]: { code: invite.code, expiresAt: invite.expiresAt },
      }));
    } catch (cause) {
      setFailure({
        kind,
        message:
          cause instanceof ApiError ? cause.message : t("settings.inviteFailed"),
      });
    } finally {
      setMinting(null);
    }
  }

  /**
   * The link carries the kind alongside the code. The server does not take the
   * client's word for it — the code alone decides what registration does — but
   * the registration screen has no other way to know whether the person
   * following this link is about to get a space of their own to name.
   */
  function linkTo(kind: InviteKind, code: string): string {
    const query = new URLSearchParams({ code });
    if (kind === "app") query.set("kind", "app");
    return `${location.origin}/register?${query.toString()}`;
  }

  /** The finished sentence, ready to be sent to somebody without editing. */
  function messageFor(kind: InviteKind, issued: Issued): string {
    const link = linkTo(kind, issued.code);
    const until = formats.dayMonthYearLong.format(new Date(issued.expiresAt));

    return kind === "space"
      ? t("settings.inviteShareSpaceText", {
          space: spaceName,
          link,
          code: issued.code,
          until,
        })
      : t("settings.inviteShareAppText", { link, code: issued.code, until });
  }

  /**
   * Put something on the clipboard, and say so.
   *
   * `quiet` is for the one case where nothing should pop up: the person had the
   * share sheet open and closed it. A `window.prompt` appearing right after
   * they dismissed something would read as the app arguing with them.
   */
  async function toClipboard(text: string, mark: CopiedWhat, quiet: boolean) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(mark);
    } catch {
      // Clipboard write can be refused outside a user gesture or over http;
      // `navigator.clipboard` is absent altogether on an insecure origin.
      if (!quiet) window.prompt(t("settings.inviteCopyManually"), text);
    }
  }

  /**
   * Hand the invitation over.
   *
   * The system sheet where there is one, the clipboard where there is not. A
   * dismissed sheet rejects with `AbortError`, which is not a failure and is
   * never reported as one — the text goes to the clipboard instead, so that
   * backing out of the sheet still leaves the invitation somewhere useful.
   */
  async function send(kind: InviteKind) {
    const issued = codes[kind];
    if (!issued) return;

    const message = messageFor(kind, issued);

    if (canShare()) {
      try {
        await navigator.share({ title: shareTitleFor(kind), text: message });
        // The sheet was the confirmation; a "Скопировано" here would be a lie.
        return;
      } catch (cause) {
        await toClipboard(message, "message", isAbort(cause));
        return;
      }
    }

    await toClipboard(message, "message", false);
  }

  /** The code on its own, because it also gets read out loud over a phone. */
  async function copyCode(kind: InviteKind) {
    const issued = codes[kind];
    if (!issued) return;
    await toClipboard(issued.code, "code", false);
  }

  function shareTitleFor(kind: InviteKind): string {
    return kind === "space"
      ? t("settings.inviteShareSpaceTitle", { space: spaceName })
      : t("settings.inviteShareAppTitle");
  }

  const intoSpace = kind === "space";
  const issued = codes[kind];
  const shareable = canShare();

  // A member cannot mint a way into this space, and the screen says why rather
  // than greying a button out and leaving them to guess. Anyone signed in may
  // hand out a way into the install, precisely because it opens nothing here.
  const blocked = intoSpace && !isOwner ? t("settings.inviteSpaceOwnerOnly") : null;

  const action = intoSpace
    ? issued
      ? t("settings.inviteSpaceReissue")
      : t("settings.inviteSpaceCreate")
    : issued
      ? t("settings.inviteAppReissue")
      : t("settings.inviteAppCreate");

  const sendLabel = intoSpace
    ? shareable
      ? t("settings.inviteSpaceShareLabel", { space: spaceName })
      : t("settings.inviteSpaceCopyLabel", { space: spaceName })
    : shareable
      ? t("settings.inviteAppShareLabel")
      : t("settings.inviteAppCopyLabel");

  return (
    <Card>
      <CardHeader title={t("settings.invite")} meta={t("settings.inviteMeta")} />
      <div className="flex flex-col gap-3 px-4 py-4">
        <p className="text-[0.8125rem] text-muted">{t("settings.inviteNote")}</p>

        <div
          role="radiogroup"
          aria-label={t("settings.inviteWhich")}
          className="flex gap-1"
        >
          <InviteChoice
            label={t("settings.inviteSpaceShort")}
            name={t("settings.inviteSpaceTitle", { space: spaceName })}
            checked={intoSpace}
            onSelect={() => setKind("space")}
          />
          <InviteChoice
            label={t("settings.inviteAppTitle")}
            checked={!intoSpace}
            onSelect={() => setKind("app")}
          />
        </div>

        <p
          className={cn(
            "flex items-start gap-3 rounded-row px-3 py-2.5 text-[0.8125rem]",
            intoSpace
              ? "bg-accent-soft text-accent-ink"
              : "bg-surface-alt text-ink-soft",
          )}
        >
          {intoSpace ? (
            <InviteSpaceIcon
              aria-hidden="true"
              className="mt-0.5 size-5 shrink-0"
              strokeWidth={1.8}
            />
          ) : (
            <InviteAppIcon
              aria-hidden="true"
              className="mt-0.5 size-5 shrink-0"
              strokeWidth={1.8}
            />
          )}
          <span>
            {intoSpace
              ? t("settings.inviteSpaceWhat", { space: spaceName })
              : t("settings.inviteAppWhat")}
          </span>
        </p>

        {blocked ? <p className="text-[0.8125rem] text-muted">{blocked}</p> : null}
        {failure?.kind === kind ? (
          <p className="text-[0.875rem] text-neg">{failure.message}</p>
        ) : null}

        {issued ? (
          <>
            {/*
              The code chip is the button that copies it — a separate
              «Копировать» beside it would be a second control for a thing
              already sitting right there, and this way the whole 44px row is
              the target.
            */}
            <button
              type="button"
              onClick={() => copyCode(kind)}
              aria-label={
                intoSpace
                  ? t("settings.inviteSpaceCodeCopy", { space: spaceName })
                  : t("settings.inviteAppCodeCopy")
              }
              className={cn(
                "flex min-h-[var(--touch)] w-full items-center gap-3 rounded-row",
                "bg-surface-alt px-4 py-3 text-left transition-colors hover:bg-line",
              )}
            >
              <code className="min-w-0 flex-1 truncate font-mono tracking-wider">
                {issued.code}
              </code>
              <CopyIcon aria-hidden="true" className="size-5 shrink-0 text-muted" />
            </button>

            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => send(kind)} aria-label={sendLabel}>
                {shareable ? (
                  <ShareIcon className="size-5" />
                ) : (
                  <CopyIcon className="size-5" />
                )}
                {shareable
                  ? t("settings.inviteShare")
                  : t("settings.inviteCopyMessage")}
              </Button>

              {/*
                The square shares this row rather than taking one of its own:
                on a phone the two ways of handing an invitation over cost two
                44px rows, and only one of them needs words on it.
              */}
              {intoSpace && spaceId ? (
                <Button
                  variant="secondary"
                  className="px-3"
                  aria-expanded={square}
                  aria-controls={squareId}
                  aria-label={
                    square ? t("settings.inviteQrHide") : t("settings.inviteQrShow")
                  }
                  onClick={() => setSquare((shown) => !shown)}
                >
                  <QrIcon aria-hidden="true" className="size-5" strokeWidth={1.8} />
                </Button>
              ) : null}

              {/*
                Mounted whether or not it has anything to say, so that a screen
                reader is already watching this spot when the word appears.
              */}
              <span role="status" className="text-[0.8125rem] text-muted">
                {copied === "code"
                  ? t("settings.inviteCopiedCode")
                  : copied === "message"
                    ? t("settings.inviteCopiedMessage")
                    : ""}
              </span>
            </div>

            {/* `hidden` rather than unmounted, so the id `aria-controls` names
                is always there; the square itself is mounted only when open,
                and that is what keeps the key out of memory in between. */}
            <div id={squareId} hidden={!square}>
              {square && intoSpace && spaceId ? (
                <InviteQr
                  spaceId={spaceId}
                  invite={issued.code}
                  link={linkTo("space", issued.code)}
                />
              ) : null}
            </div>
          </>
        ) : null}

        <Button
          variant={issued ? "ghost" : "secondary"}
          className={cn("self-start", issued && "px-2 text-[0.875rem]")}
          disabled={blocked !== null || minting !== null}
          onClick={() => mint(kind)}
        >
          {minting === kind ? t("settings.inviteMinting") : action}
        </Button>
      </div>
    </Card>
  );
}

/**
 * One of the two doors, as a pill.
 *
 * Built like the theme and language pickers further down, because it is the
 * same shape of decision and the screen should not invent a second way of
 * asking one. `name` carries the longer, unambiguous title where the visible
 * label had to be shortened to fit a phone; it contains the visible words, so
 * anybody driving this by voice still gets what they see.
 */
function InviteChoice({
  label,
  name,
  checked,
  onSelect,
}: {
  label: string;
  name?: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      aria-label={name}
      onClick={onSelect}
      className={cn(
        "min-h-[var(--touch)] flex-1 rounded-card px-3 text-[0.875rem] font-medium transition-colors",
        checked ? "bg-accent-soft text-accent-ink" : "text-muted hover:bg-surface-alt",
      )}
    >
      {label}
    </button>
  );
}

/** A minted code and the moment it stops working, kept together. */
interface Issued {
  code: string;
  expiresAt: string;
}

/**
 * Which of the two things here was just put on the clipboard.
 *
 * No kind alongside it any more: one code is on screen at a time, so there is
 * only ever one place the word can belong to.
 */
type CopiedWhat = "message" | "code";

/**
 * Whether this device can hand something to another app.
 *
 * Asked at render rather than once at import: `navigator.share` is missing on
 * most desktop browsers and on every insecure origin, and the button says a
 * different word depending on the answer.
 */
function canShare(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

/**
 * A share sheet that was simply closed.
 *
 * `navigator.share` rejects with `AbortError` for that, the same way it rejects
 * for a real failure — and closing a sheet is not a failure. Checked by name
 * rather than by `instanceof DOMException`, because what a browser throws here
 * is not guaranteed to be one.
 */
function isAbort(cause: unknown): boolean {
  return (
    typeof cause === "object" &&
    cause !== null &&
    (cause as { name?: unknown }).name === "AbortError"
  );
}

/**
 * The same invitation, held up to the other phone instead of sent to it.
 *
 * The message and this square lead to the same place, which is why they live in
 * one block. What may travel in them is not the same thing, and that is the one
 * rule here: a message is forwarded through somebody else's chat, so the key can
 * never be in it — put it there and end-to-end encryption comes down to whoever
 * can read that conversation. A square is shown from one screen to another and
 * never leaves the room, so it is the only place a key may ride.
 *
 * What the square carries therefore depends on what this device actually has:
 *
 *   - an unencrypted space: the invitation link, the same one the message
 *     carries. One scan and the person is on the sign-up screen, code filled in;
 *   - encrypted, and the key is here: `/join` with the invitation in the query
 *     and the key in the fragment (see `keyedJoinUrl`) — one scan lets them in
 *     and unlocks the space in the same motion, which is what the warning under
 *     it is about;
 *   - encrypted, and no key on this device: the invitation alone, and a line
 *     saying so. Pretending otherwise would hand somebody a way in and leave
 *     them staring at a locked space with no idea why.
 *
 * Folded away until asked for, and mounted only while it is open. The main
 * screen here is a phone, where an always-open square would push the code and
 * the buttons off the bottom — and it is wanted only when the other person is
 * standing right there. The button that opens it lives up in `InvitePanel`,
 * beside the share button, because handing an invitation over is one row of
 * choices and not two; unmounting is what drops the key from memory when the
 * square is put away.
 */
function InviteQr({
  spaceId,
  invite,
  link,
}: {
  spaceId: string;
  invite: string;
  link: string;
}) {
  const t = useT();
  const mode = useEncryptionMode(spaceId);
  const { presence } = useSpaceKey(spaceId);

  // `null` is "not looked yet", `{ material: null }` is "looked, and this
  // device has nothing" — the two lead to different words on screen.
  const [key, setKey] = useState<{ material: string | null } | null>(null);

  useEffect(() => {
    if (mode !== "e2ee" || presence !== "present") {
      setKey(null);
      return;
    }

    let alive = true;
    void keyMaterialFor(spaceId).then((material) => {
      if (alive) setKey({ material });
    });

    return () => {
      alive = false;
    };
  }, [mode, presence, spaceId]);

  const material = mode === "e2ee" && presence === "present" ? key?.material : null;
  /*
   * Nothing is drawn while the answer is still coming. Showing the plain
   * invitation for the half-second it takes to read the key would put a
   * different square in front of a camera that is already pointed at it.
   */
  const waiting =
    mode === "e2ee" && (presence === "checking" || (presence === "present" && !key));

  return (
    <div className="flex flex-col items-center gap-3">
      {waiting ? (
        <span aria-hidden="true" className="size-6 animate-pulse rounded-full bg-line" />
      ) : (
        <>
          <p className="self-stretch text-[0.875rem] text-ink-soft">
            {t("settings.inviteQrAim")}
          </p>
          <QrCode
            value={material ? keyedJoinUrl(spaceId, invite, material) : link}
            label={t("settings.inviteQrAim")}
          />
          {material ? (
            <p className="self-stretch rounded-row bg-surface-alt px-4 py-3 text-[0.8125rem] text-ink-soft">
              {t("encryption.qrWarning")}
            </p>
          ) : null}
          <p className="self-stretch text-[0.8125rem] text-muted">
            {material
              ? t("settings.inviteQrKeyed")
              : mode === "e2ee"
                ? t("settings.inviteQrNoKey")
                : t("settings.inviteQrPlain")}
          </p>
        </>
      )}
    </div>
  );
}

/**
 * The address that both lets somebody in and opens the space for them.
 *
 * The key sits **in the fragment**, after the hash, and nowhere else. Browsers
 * do not send a fragment to the server: it reaches no access log and no
 * `Referer`, and the join screen wipes it the moment it has read it. A key in
 * the path or the query would mean the server holding both the unreadable
 * records and the key to them, which is the whole point of the encryption gone
 * at the last step. It is also why this address is only ever a square and never
 * the message — a message gets forwarded.
 *
 * The invitation goes in the query instead: the server checks it anyway, and it
 * is no secret.
 */
function keyedJoinUrl(spaceId: string, invite: string, material: string): string {
  const query = new URLSearchParams({ space: spaceId, invite });

  return `${location.origin}/join?${query.toString()}#k=${material}`;
}

/**
 * Light, dark, or the system's choice.
 *
 * "Match the system" is the default and is not the same as picking light: it
 * keeps following the OS, so an evening switch to dark is followed here too.
 */
function ThemeChoice() {
  const { theme, setTheme } = useTheme();
  const t = useT();

  return (
    <Card>
      <CardHeader title={t("settings.appearance")} />
      <div
        role="radiogroup"
        aria-label={t("settings.appearance")}
        className="flex gap-1 p-2"
      >
        {THEMES.map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={theme === option}
            onClick={() => setTheme(option)}
            className={cn(
              "min-h-[var(--touch)] flex-1 rounded-card px-3 text-[0.875rem] font-medium transition-colors",
              theme === option
                ? "bg-accent-soft text-accent-ink"
                : "text-muted hover:bg-surface-alt",
            )}
          >
            {t(THEME_LABEL[option])}
          </button>
        ))}
      </div>
    </Card>
  );
}

/**
 * Russian, English, or whatever the browser says.
 *
 * Built like the theme picker next door because it is the same decision shape —
 * three states where the middle one keeps following something else. The two
 * languages are written in themselves rather than translated: that is what lets
 * somebody who cannot read the current interface find their way out of it.
 */
function LanguageChoice() {
  const choice = useLocaleChoice();
  const t = useT();

  const options = [
    { value: "system" as const, label: t("settings.localeSystem") },
    ...LOCALES.map((locale) => ({ value: locale, label: LOCALE_LABEL[locale] })),
  ];

  return (
    <Card>
      <CardHeader title={t("settings.language")} />
      <div role="radiogroup" aria-label={t("settings.language")} className="flex gap-1 p-2">
        {options.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={choice === value}
            onClick={() => setLocaleChoice(value)}
            className={cn(
              "min-h-[var(--touch)] flex-1 rounded-card px-3 text-[0.875rem] font-medium transition-colors",
              choice === value
                ? "bg-accent-soft text-accent-ink"
                : "text-muted hover:bg-surface-alt",
            )}
          >
            {label}
          </button>
        ))}
      </div>
    </Card>
  );
}

/** The feed lives off the settings screen: it is something you go and look at. */
function ActivityLink() {
  const t = useT();

  return (
    <Card>
      <Link
        to="/activity"
        className="flex min-h-[var(--touch)] items-center justify-between px-4 py-3 transition-colors hover:bg-surface-alt"
      >
        <span>{t("settings.activity")}</span>
        <span className="text-[0.875rem] text-muted">{t("settings.activityMeta")}</span>
      </Link>
    </Card>
  );
}

/**
 * Shown only in a browser tab. Once the app is installed the media query flips
 * and this disappears — a permanent "install me" banner inside the installed
 * app is the classic PWA embarrassment.
 */
function InstallHint() {
  const [standalone, setStandalone] = useState(true);
  const t = useT();

  useEffect(() => {
    const mq = window.matchMedia("(display-mode: standalone)");
    const update = () =>
      setStandalone(
        mq.matches ||
          // iOS Safari does not report display-mode until recently.
          (navigator as Navigator & { standalone?: boolean }).standalone === true,
      );
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  if (standalone) return null;

  return (
    <Card>
      <CardHeader title={t("settings.installTitle")} />
      <div className="flex flex-col gap-2 px-4 py-3 text-[0.875rem] text-muted">
        <p>
          <b className="font-medium text-ink-soft">iPhone:</b> {t("settings.installIos")}
        </p>
        <p>
          <b className="font-medium text-ink-soft">Android:</b> {t("settings.installAndroid")}
        </p>
        <p>
          <b className="font-medium text-ink-soft">macOS:</b> {t("settings.installMac")}
        </p>
      </div>
    </Card>
  );
}

/**
 * A person, as a circle: their picture if they have one, their initials if not.
 *
 * The initials are not a placeholder waiting to be replaced — most people never
 * upload anything, and a row of amber monograms is the intended look of a member
 * list rather than a gap in it. A picture simply takes the same circle.
 *
 * `src` is always a path on our own origin (`/api/media/…`), minted by the
 * server; nothing here builds one, and nothing here would render an address
 * from anywhere else.
 */
function Avatar({
  name,
  src,
  size = "row",
}: {
  name: string;
  src?: string | null;
  size?: "row" | "lead";
}) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  const box = size === "lead" ? "size-16" : "size-10";

  if (src) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        // `object-cover` because a person's photo is any shape and a circle is
        // one: letterboxing a face inside it looks broken in a way cropping does
        // not. The tinted ground shows while the bytes are still coming.
        className={cn(box, "shrink-0 rounded-full bg-accent-soft object-cover")}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        box,
        "grid shrink-0 place-items-center rounded-full",
        "bg-accent-soft font-display text-accent-ink",
        size === "lead" ? "text-[1.375rem]" : "text-[0.9375rem]",
      )}
    >
      {initials}
    </span>
  );
}
